import React, { useState } from 'react';

import { useTranslation } from 'react-i18next';

import FormControl from '@mui/material/FormControl';

import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Box from '@mui/material/Box';

import TextInput from '@common/components/TextInput/TextInput';
import SelectInput from '@common/components/SelectInput/SelectInput';
import { AVAILABLE_LANGS, PDF_FORMAT_OPTIONS } from '@common/constants';
import { isTemplateTypeEmail } from '@common/helpers/helpers';
import useTranslatedOptions from '@hooks/useTranslatedOptions/useTranslatedOptions';
import { useTemplateDetailsStore } from '@store/templateDetailsStore';
import { selectBackgroundColor } from '@common/providers/ThemeProvider/selectors';
import {
  Alert,
  Button,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import useAvailableLanguageOptions from '@hooks/useAvailableLanguageOptions';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import StarIcon from '@mui/icons-material/Star';

const LangAction = ({
  isDefault,
  onSetAsDefaultClick = () => {},
  onDeleteLangClick = () => {},
}) => {
  const { t } = useTranslation();
  return isDefault ? null : (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Button variant='text' sx={{ p: 0, mr: 1 }} onClick={onSetAsDefaultClick}>
        <Typography variant='caption'>{t('set_as_default')}</Typography>
      </Button>

      <IconButton onClick={onDeleteLangClick}>
        <DeleteForeverIcon />
      </IconButton>
    </Box>
  );
};

const FormLanguages = ({ handleOnBlur, touched, fieldsStyle, validation }) => {
  const { t } = useTranslation();
  const availableLangOptions = useAvailableLanguageOptions();
  const addLanguage = useTemplateDetailsStore((s) => s.addLanguage);
  const deleteLanguage = useTemplateDetailsStore((s) => s.deleteLanguage);
  const setDefaultTemplateLang = useTemplateDetailsStore(
    (s) => s.setDefaultTemplateLang,
  );

  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const onAddLang = (value) => {
    value && addLanguage(value);
    setAnchorEl(null);
  };

  return (
    <>
      <Divider sx={{ marginBottom: 1.8, marginTop: 1 }} />
      <Typography variant='h5' gutterBottom sx={{ mb: 0 }}>
        {t('lang_tab')}*
      </Typography>
      <Typography
        sx={{
          ...fieldsStyle,
          color: 'grey',
          fontWeight: 400,
          fontSize: '12px',
        }}
      >
        {t('lang_instructions')}
      </Typography>

      <Stack sx={{ mb: 2 }} spacing={2}>
        {validation?.defaultLanguage && (
          <Alert severity='error' size='small'>
            {t('ValidationDefaultLanguage')}
          </Alert>
        )}
        {availableLangOptions.map((langOption) => (
          <Alert
            key={langOption.value}
            severity='info'
            size='small'
            icon={<LanguageIcon />}
            action={
              <LangAction
                isDefault={langOption?.isDefault}
                onSetAsDefaultClick={() => {
                  setDefaultTemplateLang(langOption.value);
                }}
                onDeleteLangClick={() => {
                  deleteLanguage(langOption.value);
                }}
              />
            }
          >
            <Box sx={{ display: 'flex' }}>
              {t(langOption.label)}{' '}
              {langOption?.isDefault && (
                <Box
                  sx={{
                    ml: 1,
                    backgroundColor: (theme) => theme.palette?.primary?.light,
                    pl: 1,
                    pr: 0.8,
                    borderRadius: '3px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  {t('default')} <StarIcon fontSize='10px' />
                </Box>
              )}
            </Box>
          </Alert>
        ))}
      </Stack>

      <Button
        id='demo-positioned-button'
        aria-controls={open ? 'demo-positioned-menu' : undefined}
        aria-haspopup='true'
        aria-expanded={open}
        onClick={handleClick}
        variant='contained'
      >
        {t('add_language')}
      </Button>
      <Menu
        id='demo-positioned-menu'
        aria-labelledby='demo-positioned-button'
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {AVAILABLE_LANGS.map((option) => {
          if (
            availableLangOptions?.find(({ value }) => option?.value === value)
          ) {
            return (
              <MenuItem
                key={option?.value}
                disabled={true}
                onClick={() => onAddLang(option?.value)}
              >
                {t(option.label)}
              </MenuItem>
            );
          }
          return (
            <MenuItem
              key={option?.value}
              onClick={() => onAddLang(option?.value)}
            >
              {t(option.label)}
            </MenuItem>
          );
        })}
      </Menu>
    </>
  );
};

const CreateDetailsForm = ({ validation = {}, isEditMode }) => {
  const { t } = useTranslation();
  const {
    setFormData,
    setSourceType,
    sourceTypeOptions,
    setPdfAdditionalFields,
    type,
    dataType,
    description,
    name,
    pdfAdditionalFields,
  } = useTemplateDetailsStore();

  const [touched, setTouched] = useState({});

  const translatedOptions = useTranslatedOptions();

  const handleOnChange = ({ target }) => {
    const { name: field, value } = target;

    if (field === 'source') {
      const emailTypeData = sourceTypeOptions.find(
        (type) => type.source_id === value,
      );

      return setSourceType({
        dataTypeId: emailTypeData?.id,
        sourceId: emailTypeData?.source_id,
      });
    }

    if (field === 'format' || field === 'orientation') {
      return setPdfAdditionalFields({ field, value });
    }

    setFormData({
      field,
      value,
    });
  };

  const handleOnBlur = (e) => {
    const { name: field } = e.target;
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const fieldsStyle = { alignItems: null, pb: 3 };

  return (
    <Box
      sx={{
        maxWidth: '600px',
        minWidth: '300px',
        margin: '0 auto',
        width: '60%',
        backgroundColor: selectBackgroundColor,
        mt: 4,
        mb: 4,
        p: 4,
        pt: 3,
        pb: 1,
        borderRadius: '5px',
        height: '700px',
      }}
    >
      <Typography variant='h5' gutterBottom sx={fieldsStyle}>
        {t('template_information')}
      </Typography>
      <FormControl fullWidth size='small' sx={fieldsStyle}>
        <TextInput
          name={'name'}
          label={t('template_name')}
          required
          value={name || ''}
          onChange={handleOnChange}
          errors={validation?.errors}
          onBlur={handleOnBlur}
          touched={touched}
        />
      </FormControl>

      {/* <Grid size={{ xs: 12, md: 6 }} sx={{ mb: 1 }}>
        <FormControl fullWidth size='small' sx={{ alignItems: null }}>
          <SelectInput
            name={'templateType'}
            label={t('template_type')}
            accessors={{ textAccessor: 'label' }}
            onChange={handleOnChange}
            options={translatedOptions.templateType}
            required
            value={type}
            errors={validation?.errors}
            onBlur={handleOnBlur}
            touched={touched}
          />
        </FormControl>
      </Grid> */}

      <FormControl fullWidth size='small' sx={fieldsStyle}>
        <SelectInput
          disabled={isEditMode}
          name={'source'}
          label={t('template_type')}
          accessors={{ textAccessor: 'name', valueAccessor: 'source_id' }}
          onChange={handleOnChange}
          options={sourceTypeOptions}
          required
          value={dataType?.source_id || ''}
          errors={validation?.errors}
          onBlur={handleOnBlur}
          touched={touched}
        />
      </FormControl>

      {type && !isTemplateTypeEmail(type) && (
        <>
          <FormControl fullWidth size='small' sx={fieldsStyle}>
            <SelectInput
              name={'format'}
              label={t('template_format')}
              accessors={{ textAccessor: 'name' }}
              onChange={handleOnChange}
              options={PDF_FORMAT_OPTIONS}
              required
              value={pdfAdditionalFields?.format || ''}
              errors={validation?.errors}
              onBlur={handleOnBlur}
              touched={touched}
            />
          </FormControl>

          <FormControl fullWidth size='small' sx={fieldsStyle}>
            <SelectInput
              name={'orientation'}
              label={t('template_orientation')}
              accessors={{ textAccessor: 'label' }}
              onChange={handleOnChange}
              options={translatedOptions.pdfOrientation}
              required
              value={pdfAdditionalFields?.orientation || ''}
              errors={validation?.errors}
              onBlur={handleOnBlur}
              touched={touched}
            />
          </FormControl>
        </>
      )}

      <FormControl fullWidth size='small' sx={fieldsStyle}>
        <TextInput
          name={'description'}
          label={t('description')}
          multiline={true}
          rows={4}
          column={6}
          value={description || ''}
          onChange={handleOnChange}
          errors={validation?.errors}
          onBlur={handleOnBlur}
          touched={touched}
        />
      </FormControl>
      <FormLanguages
        handleOnChange={handleOnChange}
        handleOnBlur={handleOnBlur}
        touched={touched}
        fieldsStyle={fieldsStyle}
        validation={validation}
      />
    </Box>
  );
};

export default CreateDetailsForm;
